(function (root, factory) {
  const codec = typeof module !== "undefined" && module.exports
    ? require("./codec.js")
    : root.Codec;
  const api = factory(codec);
  if (typeof module !== "undefined" && module.exports) {
    module.exports = api;
  } else {
    root.Rtsp = api;
  }
}(typeof globalThis !== "undefined" ? globalThis : this, (Codec) => {
  if (!Codec) throw new Error("codec.js must be loaded before rtsp.js");
  const { alawByteToPcm16, mulawByteToPcm16, G711AudioSink } = Codec;
  const MAX_AU = 4 * 1024 * 1024;
  const MAX_MEDIA_PACKET_BYTES = 65535;
  const MAX_STATUS_LENGTH = 256;
  const START = Uint8Array.of(0, 0, 0, 1);
  const DEFAULT_RTP_JITTER_BUFFER_MS = 300;
  const MAX_RTP_JITTER_BUFFER_MS = 2000;
  const MAX_JITTER_PACKETS_PER_SOURCE = 4096;
  const MAX_FRAME_DURATION_MS = 1000;
  const DEFAULT_VIDEO_FPS = 30;

  function normalizeRtpJitterBuffer(value) {
    if (value === undefined || value === null || value === "") return DEFAULT_RTP_JITTER_BUFFER_MS;
    const parsed = typeof value === "number" ? value : Number(String(value).trim());
    if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < 0) return DEFAULT_RTP_JITTER_BUFFER_MS;
    return Math.min(parsed, MAX_RTP_JITTER_BUFFER_MS);
  }

  function normalizeVideoFps(value) {
    if (value === undefined || value === null || value === "") return DEFAULT_VIDEO_FPS;
    const parsed = typeof value === "number" ? value : Number(String(value).trim());
    return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_VIDEO_FPS;
  }

  function requireString(value, name) {
    if (typeof value !== "string" || !value) {
      throw new TypeError(`${name} must be a non-empty string`);
    }
    return value;
  }

  function validateStreamId(streamId) {
    if (!Number.isInteger(streamId) || streamId < 1 || streamId > 5) {
      throw new RangeError("stream ID must be an integer from 1 through 5");
    }
    return streamId;
  }

  function buildViewerWsUrl(relay, protocol) {
    if (!relay || typeof relay !== "object") {
      throw new TypeError("relay response is required");
    }
    // Prefer Beluga clientStreamUrl when present (orca); fall back to host/port assembly.
    if (typeof relay.clientStreamUrl === "string" && relay.clientStreamUrl) {
      const clientStreamUrl = requireString(relay.clientStreamUrl, "client stream URL");
      if (/\s/.test(clientStreamUrl) || clientStreamUrl.trim() !== clientStreamUrl) {
        throw new TypeError("client stream URL must not contain whitespace");
      }
      let endpointUrl;
      try {
        endpointUrl = new URL(clientStreamUrl);
      } catch (_error) {
        throw new TypeError("client stream URL must be a valid URL");
      }
      if (endpointUrl.protocol !== "ws:" && endpointUrl.protocol !== "wss:") {
        throw new TypeError("client stream URL must be a WebSocket endpoint");
      }
      if (!endpointUrl.hostname || endpointUrl.username || endpointUrl.password) {
        throw new TypeError("client stream URL must not contain credentials");
      }
      if (endpointUrl.search || endpointUrl.hash) {
        throw new TypeError("client stream URL must not contain query or fragment");
      }
      return clientStreamUrl;
    }
    const hostAddr = requireString(relay.hostAddr, "relay host");
    if (/[\\/\\?#@\s]/.test(hostAddr) || hostAddr.includes("://")) {
      throw new TypeError("relay host must be a hostname");
    }
    if (!Number.isInteger(relay.hostPort) || relay.hostPort < 1 || relay.hostPort > 65535) {
      throw new RangeError("relay port must be an integer from 1 through 65535");
    }
    const sessionId = requireString(relay.sessionId, "session ID");
    const scheme = requireString(protocol, "relay protocol").replace(/:$/, "");
    if (scheme !== "ws" && scheme !== "wss") {
      throw new TypeError("relay protocol must be a WebSocket endpoint");
    }
    const host = hostAddr.includes(":") && !hostAddr.startsWith("[") ? `[${hostAddr}]` : hostAddr;
    const endpointUrl = new URL(`${scheme}://${host}:${relay.hostPort}`);
    endpointUrl.pathname = `/ws/viewer/${encodeURIComponent(sessionId)}`;
    endpointUrl.search = "";
    endpointUrl.hash = "";
    return endpointUrl.toString();
  }

  function boundedStatus(message) {
    return String(message).replace(/[\r\n]+/g, " ").slice(0, MAX_STATUS_LENGTH);
  }

  function toBytes(buffer) {
    return buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  }

  function decodeBase64(value) {
    const binary = typeof atob === "function"
      ? atob(value)
      : Buffer.from(value, "base64").toString("binary");
    return Uint8Array.from(binary, (character) => character.charCodeAt(0));
  }

  class BitReader {
    constructor(bytes) {
      this.bytes = bytes;
      this.position = 0;
    }

    readBit() {
      if (this.position >= this.bytes.length * 8) throw new Error("truncated video parameter set");
      const byte = this.bytes[this.position >> 3];
      const bit = (byte >> (7 - (this.position & 7))) & 1;
      this.position += 1;
      return bit;
    }

    readBits(count) {
      if (!Number.isInteger(count) || count < 0 || count > 32) throw new Error("invalid video parameter set field");
      let value = 0;
      for (let index = 0; index < count; index += 1) value = (value * 2) + this.readBit();
      return value;
    }

    readUnsignedExpGolomb() {
      let leadingZeroBits = 0;
      while (this.readBit() === 0) {
        leadingZeroBits += 1;
        if (leadingZeroBits > 31) throw new Error("invalid video parameter set code");
      }
      const suffix = leadingZeroBits ? this.readBits(leadingZeroBits) : 0;
      return (2 ** leadingZeroBits) - 1 + suffix;
    }

    readSignedExpGolomb() {
      const value = this.readUnsignedExpGolomb();
      return value & 1 ? (value + 1) / 2 : -(value / 2);
    }
  }

  function removeEmulationPrevention(bytes, headerBytes) {
    const rbsp = [];
    let zeroCount = 0;
    for (let index = headerBytes; index < bytes.length; index += 1) {
      const byte = bytes[index];
      if (zeroCount >= 2 && byte === 3 && bytes[index + 1] <= 3) {
        zeroCount = 0;
        continue;
      }
      rbsp.push(byte);
      zeroCount = byte === 0 ? zeroCount + 1 : 0;
    }
    return Uint8Array.from(rbsp);
  }

  function skipH264ScalingList(reader, size) {
    let lastScale = 8;
    let nextScale = 8;
    for (let index = 0; index < size; index += 1) {
      if (nextScale !== 0) {
        const deltaScale = reader.readSignedExpGolomb();
        nextScale = (lastScale + deltaScale + 256) % 256;
      }
      lastScale = nextScale === 0 ? lastScale : nextScale;
    }
  }

  const H264_HIGH_PROFILES = new Set([100, 110, 122, 244, 44, 83, 86, 118, 128, 138, 139, 134, 135]);

  function h264Dimensions(nal) {
    if (!nal || nal.length < 2 || (nal[0] & 31) !== 7) return null;
    const reader = new BitReader(removeEmulationPrevention(nal, 1));
    const profileIdc = reader.readBits(8);
    reader.readBits(8); // constraint flags and reserved bits
    reader.readBits(8); // level_idc
    reader.readUnsignedExpGolomb(); // seq_parameter_set_id

    let chromaFormatIdc = 1;
    let separateColourPlane = false;
    if (H264_HIGH_PROFILES.has(profileIdc)) {
      chromaFormatIdc = reader.readUnsignedExpGolomb();
      if (chromaFormatIdc > 3) throw new Error("invalid H264 chroma format");
      if (chromaFormatIdc === 3) separateColourPlane = reader.readBit() === 1;
      reader.readUnsignedExpGolomb(); // bit_depth_luma_minus8
      reader.readUnsignedExpGolomb(); // bit_depth_chroma_minus8
      reader.readBit(); // qpprime_y_zero_transform_bypass_flag
      if (reader.readBit()) {
        const scalingListCount = chromaFormatIdc === 3 ? 12 : 8;
        for (let index = 0; index < scalingListCount; index += 1) {
          if (reader.readBit()) skipH264ScalingList(reader, index < 6 ? 16 : 64);
        }
      }
    }

    reader.readUnsignedExpGolomb(); // log2_max_frame_num_minus4
    const picOrderCountType = reader.readUnsignedExpGolomb();
    if (picOrderCountType === 0) {
      reader.readUnsignedExpGolomb(); // log2_max_pic_order_cnt_lsb_minus4
    } else if (picOrderCountType === 1) {
      reader.readBit(); // delta_pic_order_always_zero_flag
      reader.readSignedExpGolomb(); // offset_for_non_ref_pic
      reader.readSignedExpGolomb(); // offset_for_top_to_bottom_field
      const cycleLength = reader.readUnsignedExpGolomb();
      for (let index = 0; index < cycleLength; index += 1) reader.readSignedExpGolomb();
    } else if (picOrderCountType !== 2) {
      throw new Error("invalid H264 picture order count");
    }

    reader.readUnsignedExpGolomb(); // max_num_ref_frames
    reader.readBit(); // gaps_in_frame_num_value_allowed_flag
    const widthInMbs = reader.readUnsignedExpGolomb() + 1;
    const heightInMapUnits = reader.readUnsignedExpGolomb() + 1;
    const frameMbsOnly = reader.readBit() === 1;
    if (!frameMbsOnly) reader.readBit(); // mb_adaptive_frame_field_flag
    reader.readBit(); // direct_8x8_inference_flag

    let cropLeft = 0;
    let cropRight = 0;
    let cropTop = 0;
    let cropBottom = 0;
    if (reader.readBit()) {
      cropLeft = reader.readUnsignedExpGolomb();
      cropRight = reader.readUnsignedExpGolomb();
      cropTop = reader.readUnsignedExpGolomb();
      cropBottom = reader.readUnsignedExpGolomb();
    }

    const subWidthC = chromaFormatIdc === 0 || chromaFormatIdc === 3 ? 1 : 2;
    const subHeightC = chromaFormatIdc === 0 || chromaFormatIdc === 2 || chromaFormatIdc === 3 ? 1 : 2;
    const cropUnitX = separateColourPlane || chromaFormatIdc === 0 ? 1 : subWidthC;
    const cropUnitY = separateColourPlane || chromaFormatIdc === 0
      ? (frameMbsOnly ? 1 : 2)
      : subHeightC * (frameMbsOnly ? 1 : 2);
    const width = widthInMbs * 16 - (cropLeft + cropRight) * cropUnitX;
    const height = heightInMapUnits * 16 * (frameMbsOnly ? 1 : 2) - (cropTop + cropBottom) * cropUnitY;
    if (!Number.isSafeInteger(width) || !Number.isSafeInteger(height) || width <= 0 || height <= 0) {
      throw new Error("invalid H264 dimensions");
    }
    return { width, height };
  }

  function skipH265ProfileTierLevel(reader, maxSubLayers) {
    reader.readBits(2); // general_profile_space
    reader.readBit(); // general_tier_flag
    reader.readBits(5); // general_profile_idc
    reader.readBits(32); // general_profile_compatibility_flags
    reader.readBits(16); // first half of general_constraint_indicator_flags
    reader.readBits(32); // second half of general_constraint_indicator_flags
    reader.readBits(8); // general_level_idc
    const profilePresent = [];
    const levelPresent = [];
    for (let index = 0; index < maxSubLayers; index += 1) {
      profilePresent[index] = reader.readBit() === 1;
      levelPresent[index] = reader.readBit() === 1;
    }
    // The reserved_zero_2bits fields are present only when the SPS advertises
    // one or more sub-layers.  Reading them for max_sub_layers_minus1 == 0
    // shifts the SPS dimensions into the wrong bit positions (for example,
    // 1920x1080 becomes 63x11 on some cameras).
    if (maxSubLayers > 0) {
      for (let index = maxSubLayers; index < 8; index += 1) reader.readBits(2);
    }
    for (let index = 0; index < maxSubLayers; index += 1) {
      if (profilePresent[index]) {
        reader.readBits(2); // sub_layer_profile_space
        reader.readBit(); // sub_layer_tier_flag
        reader.readBits(5); // sub_layer_profile_idc
        reader.readBits(32); // sub_layer_profile_compatibility_flags
        reader.readBits(16); // sub_layer_constraint_indicator_flags
        reader.readBits(32);
      }
      if (levelPresent[index]) reader.readBits(8); // sub_layer_level_idc
    }
  }

  function h265Dimensions(nal) {
    if (!nal || nal.length < 3 || ((nal[0] >> 1) & 63) !== 33) return null;
    const reader = new BitReader(removeEmulationPrevention(nal, 2));
    reader.readBits(4); // sps_video_parameter_set_id
    const maxSubLayers = reader.readBits(3);
    reader.readBit(); // sps_temporal_id_nesting_flag
    skipH265ProfileTierLevel(reader, maxSubLayers);
    reader.readUnsignedExpGolomb(); // sps_seq_parameter_set_id
    const chromaFormatIdc = reader.readUnsignedExpGolomb();
    if (chromaFormatIdc > 3) throw new Error("invalid H265 chroma format");
    if (chromaFormatIdc === 3) reader.readBit(); // separate_colour_plane_flag
    const widthInSamples = reader.readUnsignedExpGolomb();
    const heightInSamples = reader.readUnsignedExpGolomb();
    let cropLeft = 0;
    let cropRight = 0;
    let cropTop = 0;
    let cropBottom = 0;
    if (reader.readBit()) {
      cropLeft = reader.readUnsignedExpGolomb();
      cropRight = reader.readUnsignedExpGolomb();
      cropTop = reader.readUnsignedExpGolomb();
      cropBottom = reader.readUnsignedExpGolomb();
    }
    const subWidthC = chromaFormatIdc === 0 || chromaFormatIdc === 3 ? 1 : 2;
    const subHeightC = chromaFormatIdc === 0 || chromaFormatIdc === 2 || chromaFormatIdc === 3 ? 1 : 2;
    const width = widthInSamples - (cropLeft + cropRight) * subWidthC;
    const height = heightInSamples - (cropTop + cropBottom) * subHeightC;
    if (!Number.isSafeInteger(width) || !Number.isSafeInteger(height) || width <= 0 || height <= 0) {
      throw new Error("invalid H265 dimensions");
    }
    return { width, height };
  }

  function parseVideoDimensions(codecName, parameterSets) {
    const parse = codecName === "h265" ? h265Dimensions : h264Dimensions;
    for (const encoded of parameterSets || []) {
      try {
        const dimensions = parse(decodeBase64(encoded));
        if (dimensions) return dimensions;
      } catch (_error) {
        // Some cameras omit SPS dimensions or send a decoder-specific variant.
        // Keep SDP valid and let the decoder recover from in-band parameter sets.
      }
    }
    return {};
  }

  function parseStreamSdp(sdp) {
    if (typeof sdp !== "string" || !sdp.trim()) throw new Error("invalid SDP");
    const sections = [];
    let current = null;
    for (const rawLine of sdp.split(/\r?\n/)) {
      const line = rawLine.trim();
      if (!line) continue;
      if (line.startsWith("m=")) {
        current = { index: sections.length, lines: [line] };
        sections.push(current);
      } else if (current) {
        current.lines.push(line);
      }
    }
    if (!sections.length) throw new Error("invalid SDP media description");

    const tracks = [];
    const activePayloadTypes = new Set();
    for (const section of sections) {
      const sourceIds = new Map();
      for (const line of section.lines) {
        if (!line.startsWith("a=ssrc:")) continue;
        const match = /^a=ssrc:(\d+)\s+(.+)$/.exec(line);
        if (!match) throw new Error("invalid SDP SSRC");
        const ssrc = Number(match[1]);
        if (!Number.isSafeInteger(ssrc) || ssrc < 0 || ssrc > 0xffffffff) throw new Error("invalid SDP SSRC");
        const attributes = match[2].split(/\s+/);
        const hasCname = attributes.some((attribute) => attribute.startsWith("cname:") && attribute.length > 6);
        sourceIds.set(ssrc, (sourceIds.get(ssrc) || false) || hasCname);
      }
      let ssrc = null;
      if (sourceIds.size) {
        if (sourceIds.size !== 1 || [...sourceIds.values()].some((hasCname) => !hasCname)) throw new Error("invalid SDP SSRC");
        ssrc = [...sourceIds.keys()][0];
      }

      const mediaFields = section.lines[0].slice(2).split(/\s+/);
      if (mediaFields.length < 4) throw new Error("invalid SDP media description");
      const kind = mediaFields[0].toLowerCase();
      if (kind !== "video" && kind !== "audio") throw new Error("unsupported SDP media");
      const framerateLine = section.lines.find((line) => line.startsWith("a=framerate:"));
      const payloadTypes = mediaFields.slice(3).map(Number);
      if (payloadTypes.length !== 1 || !Number.isInteger(payloadTypes[0]) || payloadTypes[0] < 0 || payloadTypes[0] > 127) {
        throw new Error("invalid SDP payload type");
      }
      const payloadType = payloadTypes[0];
      if (activePayloadTypes.has(payloadType)) throw new Error("duplicate RTP payload type");
      activePayloadTypes.add(payloadType);
      const rtpmapLine = section.lines.find((line) => line.startsWith(`a=rtpmap:${payloadType} `));
      let encoding;
      let clockRate;
      let channels;
      if (rtpmapLine) {
        const fields = rtpmapLine.slice(`a=rtpmap:${payloadType} `.length).split("/");
        encoding = fields[0];
        clockRate = Number(fields[1]);
        channels = fields[2] === undefined ? null : Number(fields[2]);
        if (!encoding || !Number.isInteger(clockRate) || clockRate <= 0 || (fields[2] !== undefined && !Number.isInteger(channels))) {
          throw new Error("invalid SDP rtpmap");
        }
      } else if (payloadType === 0 || payloadType === 8) {
        encoding = payloadType === 0 ? "PCMU" : "PCMA";
        clockRate = 8000;
        channels = 1;
      } else {
        throw new Error("missing rtpmap");
      }
      const fmtpLine = section.lines.find((line) => line.startsWith(`a=fmtp:${payloadType} `));
      const fmtp = fmtpLine ? fmtpLine.slice(`a=fmtp:${payloadType} `.length) : "";
      const parameter = (name) => {
        const part = fmtp.split(";").map((value) => value.trim()).find((value) => value.startsWith(`${name}=`));
        return part ? part.slice(name.length + 1).trim() : undefined;
      };
      const codecName = encoding.toLowerCase();
      let codec;
      let packetizationMode = null;
      let parameterSets = [];
      if (codecName === "h264") {
        if (clockRate !== 90000 || parameter("packetization-mode") !== "1") throw new Error("invalid H264 SDP parameters");
        packetizationMode = 1;
        parameterSets = (parameter("sprop-parameter-sets") || "").split(",").filter(Boolean);
        codec = "h264";
      } else if (codecName === "h265") {
        if (clockRate !== 90000) throw new Error("invalid H265 SDP parameters");
        for (const name of ["sprop-max-don-diff", "sprop-depack-buf-nalus"]) {
          const value = parameter(name);
          if (value !== undefined && (!/^\d+$/.test(value) || Number(value) !== 0)) throw new Error("invalid H265 SDP parameters");
        }
        parameterSets = ["sprop-vps", "sprop-sps", "sprop-pps"].flatMap((name) => (parameter(name) || "").split(",").filter(Boolean));
        codec = "h265";
      } else if (codecName === "pcma" || codecName === "pcmu") {
        if (clockRate !== 8000) throw new Error("invalid G711 SDP parameters");
        codec = codecName;
        channels = channels || 1;
      } else {
        throw new Error("unsupported SDP codec");
      }
      const dimensions = kind === "video" ? parseVideoDimensions(codec, parameterSets) : {};
      tracks.push({
        trackId: section.index,
        ssrc,
        kind,
        codec,
        payloadType,
        clockRate,
        channels: kind === "audio" ? (channels || 1) : null,
        packetizationMode,
        parameterSets,
        width: dimensions.width,
        height: dimensions.height,
        fps: kind === "video" ? normalizeVideoFps(framerateLine?.slice("a=framerate:".length)) : null,
      });
    }
    if (!tracks.length) throw new Error("no active SDP tracks");
    return tracks;
  }

  function parseRtpPacket(input) {
    const bytes = toBytes(input);
    if (bytes.length > MAX_MEDIA_PACKET_BYTES || bytes.length < 12 || bytes[0] >> 6 !== 2) {
      throw new Error("invalid RTP header");
    }
    const payloadType = bytes[1] & 127;
    if (payloadType >= 64 && payloadType <= 95) throw new Error("ambiguous RTP payload type");
    const csrcCount = bytes[0] & 15;
    let position = 12 + csrcCount * 4;
    if (position > bytes.length) throw new Error("truncated RTP CSRC");
    if (bytes[0] & 16) {
      if (position + 4 > bytes.length) throw new Error("truncated RTP extension");
      const extensionLength = (bytes[position + 2] << 8) | bytes[position + 3];
      position += 4 + extensionLength * 4;
      if (position > bytes.length) throw new Error("truncated RTP extension");
    }
    let end = bytes.length;
    if (bytes[0] & 32) {
      const padding = bytes[end - 1];
      if (!padding || padding > end - position) throw new Error("invalid RTP padding");
      end -= padding;
    }
    if (position >= end) throw new Error("empty RTP payload");
    return {
      marker: !!(bytes[1] & 128),
      payloadType,
      sequence: (bytes[2] << 8) | bytes[3],
      timestamp: new DataView(bytes.buffer, bytes.byteOffset + 4, 4).getUint32(0),
      ssrc: new DataView(bytes.buffer, bytes.byteOffset + 8, 4).getUint32(0),
      payload: bytes.slice(position, end),
    };
  }

  function invalidRtcp() {
    throw new Error("invalid RTCP packet");
  }

  function parseCompoundRtcp(input) {
    const bytes = toBytes(input);
    if (bytes.length > MAX_MEDIA_PACKET_BYTES || bytes.length < 4) invalidRtcp();
    const packets = [];
    let offset = 0;
    while (offset < bytes.length) {
      if (offset + 4 > bytes.length) invalidRtcp();
      const first = bytes[offset];
      const packetType = bytes[offset + 1];
      if (first >> 6 !== 2 || packetType < 192 || packetType > 223) invalidRtcp();
      const length = ((((bytes[offset + 2] << 8) | bytes[offset + 3]) + 1) * 4);
      const end = offset + length;
      if (length < 4 || end > bytes.length) invalidRtcp();
      const padding = !!(first & 0x20);
      if (padding && end !== bytes.length) invalidRtcp();
      if (padding) {
        const paddingLength = bytes[end - 1];
        if (!paddingLength || paddingLength > length - 4) invalidRtcp();
      }
      packets.push({
        packetType,
        count: first & 0x1f,
        padding,
        length,
      });
      offset = end;
    }
    if (offset !== bytes.length) invalidRtcp();
    return packets;
  }

  function classifyMediaPacket(input) {
    const bytes = toBytes(input);
    if (bytes.length > MAX_MEDIA_PACKET_BYTES || bytes.length < 2 || bytes[0] >> 6 !== 2) {
      throw new Error("invalid media packet");
    }
    if (bytes[1] >= 192 && bytes[1] <= 223) {
      return { kind: "rtcp", packets: parseCompoundRtcp(bytes) };
    }
    return { kind: "rtp", packet: parseRtpPacket(bytes) };
  }

  function concat(parts) {
    const length = parts.reduce((total, part) => total + part.length, 0);
    if (length > MAX_AU) throw new Error("partial access unit exceeds 4 MiB");
    const output = new Uint8Array(length);
    let position = 0;
    for (const part of parts) {
      output.set(part, position);
      position += part.length;
    }
    return output;
  }

  function annex(nal) {
    return concat([START, nal]);
  }

  function sequenceDistance(from, to) {
    return (to - from + 65536) & 65535;
  }

  function sequenceBefore(left, right) {
    const distance = sequenceDistance(left, right);
    return distance !== 0 && distance < 32768;
  }

  class RtpJitterBuffer {
    constructor(delayMs, maxPackets = MAX_JITTER_PACKETS_PER_SOURCE) {
      this.delayMs = normalizeRtpJitterBuffer(delayMs);
      this.maxPackets = Math.max(1, maxPackets);
      this.states = new Map();
      this.nextOrder = 0;
    }

    push(packet, arrivalTimeMs) {
      const source = `${packet.payloadType}:${packet.ssrc}`;
      let state = this.states.get(source);
      if (!state) {
        state = { expected: packet.sequence, released: false, packets: new Map() };
        this.states.set(source, state);
      }
      if (state.packets.has(packet.sequence)) return { kind: "duplicate" };
      if (state.released && sequenceBefore(packet.sequence, state.expected)) return { kind: "late" };
      if (!state.released && sequenceBefore(packet.sequence, state.expected)) state.expected = packet.sequence;
      if (state.packets.size >= this.maxPackets) {
        this.states.delete(source);
        return { kind: "overflow" };
      }
      state.packets.set(packet.sequence, { packet, arrivalTimeMs, order: this.nextOrder++ });
      return { kind: "queued" };
    }

    flush(nowMs) {
      const releasedBySsrc = [];
      for (const state of this.states.values()) {
        const released = [];
        while (state.packets.size) {
          let entry = state.packets.get(state.expected);
          if (!entry) {
            entry = [...state.packets.values()]
              .sort((left, right) => sequenceDistance(state.expected, left.packet.sequence) - sequenceDistance(state.expected, right.packet.sequence))[0];
            if (!entry || nowMs - entry.arrivalTimeMs < this.delayMs) break;
            state.expected = entry.packet.sequence;
          }
          if (nowMs - entry.arrivalTimeMs < this.delayMs) break;
          state.packets.delete(entry.packet.sequence);
          state.released = true;
          state.expected = (entry.packet.sequence + 1) & 65535;
          released.push({ packet: entry.packet, order: entry.order });
        }
        if (released.length) releasedBySsrc.push(released);
      }
      const released = [];
      while (releasedBySsrc.length) {
        let selected = 0;
        for (let index = 1; index < releasedBySsrc.length; index += 1) {
          if (releasedBySsrc[index][0].order < releasedBySsrc[selected][0].order) selected = index;
        }
        const queue = releasedBySsrc[selected];
        released.push(queue.shift().packet);
        if (!queue.length) releasedBySsrc.splice(selected, 1);
      }
      return released;
    }

    nextDelay(nowMs) {
      let next = null;
      for (const state of this.states.values()) {
        for (const entry of state.packets.values()) {
          const delay = Math.max(0, entry.arrivalTimeMs + this.delayMs - nowMs);
          next = next === null ? delay : Math.min(next, delay);
        }
      }
      return next;
    }

    reset() {
      this.states.clear();
      this.nextOrder = 0;
    }
  }

  class RtpWorkerCore {
    constructor(emit, options = {}) {
      this.emit = emit;
      this.jitter = new RtpJitterBuffer(options.rtpJitterBuffer ?? DEFAULT_RTP_JITTER_BUFFER_MS);
      this.reset();
    }

    configure(value) {
      this.jitter = new RtpJitterBuffer(normalizeRtpJitterBuffer(value));
      this.reset();
    }

    resetPartial() {
      this.au = [];
      this.auBytes = 0;
      this.auHasIdr = false;
      this.auTimestamp = null;
      this.fu = null;
      this.fuBytes = 0;
    }

    reset() {
      this.jitter.reset();
      this.info = null;
      this.tracksByPayloadType = new Map();
      this.videoReady = false;
      this.vps = null;
      this.sps = null;
      this.pps = null;
      this.lastSeq = null;
      this.awaitingAuStart = false;
      this.damagedTimestamp = null;
      this.videoClockRate = 90000;
      this.lastEmittedTimestamp = null;
      this.resetPartial();
    }

    streamInfo(info) {
      this.reset();
      const tracks = parseStreamSdp(info?.sdp);
      this.info = info;
      for (const track of tracks) {
        this.tracksByPayloadType.set(track.payloadType, track);
      }
      const video = tracks.find((track) => track.kind === "video");
      this.videoClockRate = video?.clockRate || 90000;
      if (video) {
        for (const parameterSet of video.parameterSets || []) {
          this.classifyNal(decodeBase64(parameterSet), video.codec);
        }
      }
      this.emit({
        type: "configChanged",
        mediaEpoch: info.mediaEpoch,
        version: info.version,
        videoCodec: video?.codec === "h265" ? "H265" : "H264",
        fps: video?.fps ?? DEFAULT_VIDEO_FPS,
        width: video?.width,
        height: video?.height,
      });
      this.emit({ type: "ready", mediaEpoch: info.mediaEpoch, version: info.version });
    }

    packet(buffer, arrivalTimeMs = monotonicNow()) {
      if (!this.info) return;
      const media = classifyMediaPacket(buffer);
      if (media.kind === "rtcp") return;
      const rtp = media.packet;
      const track = this.trackForRtp(rtp);
      const wire = { mediaEpoch: this.info.mediaEpoch, version: this.info.version };
      if (this.jitter.delayMs !== 0) {
        const result = this.jitter.push(rtp, arrivalTimeMs);
        if (result.kind === "overflow") {
          if (track.kind === "video") {
            this.resetPartial();
            this.lastSeq = null;
            this.awaitingAuStart = false;
            this.damagedTimestamp = null;
          }
          throw new Error("RTP jitter buffer overflow");
        }
        this.flush(arrivalTimeMs);
        return;
      }
      this.processRtp(rtp, wire, track);
    }

    trackForRtp(rtp) {
      const track = this.tracksByPayloadType.get(rtp.payloadType);
      if (!track) throw new Error("unknown RTP payload type");
      if (track.ssrc === null) {
        track.ssrc = rtp.ssrc;
      } else if (track.ssrc !== rtp.ssrc) {
        throw new Error("RTP SSRC mismatch");
      }
      return track;
    }

    flush(nowMs = monotonicNow()) {
      if (!this.info || this.jitter.delayMs === 0) return;
      for (const rtp of this.jitter.flush(nowMs)) {
        const track = this.trackForRtp(rtp);
        this.processRtp(rtp, { mediaEpoch: this.info.mediaEpoch, version: this.info.version }, track);
      }
    }

    nextFlushDelay(nowMs = monotonicNow()) {
      if (!this.info || this.jitter.delayMs === 0) return null;
      return this.jitter.nextDelay(nowMs);
    }

    processRtp(rtp, wire, track) {
      if (track.kind === "audio") {
        if (this.videoReady) {
          this.emit({
            type: "audio",
            mediaEpoch: wire.mediaEpoch,
            version: wire.version,
            timestamp: rtp.timestamp,
            codec: track.codec,
            buffer: rtp.payload.buffer,
          });
        }
        return;
      }
      this.videoPacket(rtp, wire, track.codec);
    }

    frameDuration(timestamp) {
      const previous = this.lastEmittedTimestamp;
      this.lastEmittedTimestamp = timestamp;
      if (previous === null) return null;
      const ticks = (timestamp - previous + 0x1_0000_0000) % 0x1_0000_0000;
      const milliseconds = (ticks / this.videoClockRate) * 1000;
      if (!Number.isFinite(milliseconds) || milliseconds <= 0 || milliseconds > MAX_FRAME_DURATION_MS) return null;
      return milliseconds;
    }

    videoPacket(rtp, wire, codec) {
      let discontinuity = false;
      if (this.lastSeq !== null && rtp.sequence !== ((this.lastSeq + 1) & 65535)) {
        const midAccessUnit = this.au.length > 0 || this.fu !== null;
        this.resetPartial();
        if (midAccessUnit) {
          this.awaitingAuStart = true;
          this.damagedTimestamp = rtp.timestamp;
        }
        discontinuity = true;
      }
      this.lastSeq = rtp.sequence;
      if (this.awaitingAuStart) {
        if (rtp.timestamp === this.damagedTimestamp) {
          this.resetPartial();
          return;
        }
        this.awaitingAuStart = false;
        this.damagedTimestamp = null;
      }
      if (this.auTimestamp !== null && rtp.timestamp !== this.auTimestamp) {
        this.resetPartial();
        discontinuity = true;
      }
      this.auTimestamp = rtp.timestamp;

      const nals = codec === "h265"
        ? this.h265Nals(rtp.payload, discontinuity)
        : this.h264Nals(rtp.payload, discontinuity);
      if (nals === null) return;

      for (const nal of nals) {
        this.classifyNal(nal, codec);
        const framed = annex(nal);
        if (this.auBytes + framed.length > MAX_AU) {
          this.resetPartial();
          throw new Error("partial access unit exceeds 4 MiB");
        }
        this.au.push(framed);
        this.auBytes += framed.length;
      }

      if (rtp.marker && !this.fu) {
        const idr = this.auHasIdr;
        if (idr) {
          const prefix = this.videoReady ? [] : this.parameterPrefix(codec);
          if (prefix !== null) {
            const parts = [...prefix.map((nal) => annex(nal)), ...this.au];
            if (!this.videoReady) this.videoReady = true;
            const output = concat(parts);
            this.emit({
              type: "video",
              mediaEpoch: wire.mediaEpoch,
              version: wire.version,
              timestamp: rtp.timestamp,
              duration: this.frameDuration(rtp.timestamp),
              keyframe: true,
              buffer: output.buffer,
            });
          }
        } else if (this.videoReady && this.au.length) {
          const output = concat(this.au);
          this.emit({
            type: "video",
            mediaEpoch: wire.mediaEpoch,
            version: wire.version,
            timestamp: rtp.timestamp,
            duration: this.frameDuration(rtp.timestamp),
            keyframe: false,
            buffer: output.buffer,
          });
        }
        this.resetPartial();
      }
    }

    fragment({ start, end, discontinuity, header, body, name }) {
      if (discontinuity && !start) return null;
      if (start) {
        if (this.fu) throw new Error(`nested ${name}`);
        this.fu = [header, body];
        this.fuBytes = header.length + body.length;
      } else {
        if (!this.fu) return null;
        this.fuBytes += body.length;
        if (this.auBytes + this.fuBytes + START.length > MAX_AU) {
          this.resetPartial();
          throw new Error("partial access unit exceeds 4 MiB");
        }
        this.fu.push(body);
      }
      if (!end) return null;
      const nal = concat(this.fu);
      this.fu = null;
      this.fuBytes = 0;
      return [nal];
    }

    h265Nals(payload, discontinuity) {
      if (payload.length < 2) throw new Error("malformed H265 payload");
      const type = (payload[0] >> 1) & 63;
      if (type <= 47) return [payload];
      if (type === 48) {
        const nals = [];
        let position = 2;
        if (position >= payload.length) throw new Error("malformed H265 aggregation packet");
        while (position < payload.length) {
          if (position + 2 > payload.length) throw new Error("malformed H265 aggregation packet");
          const length = (payload[position] << 8) | payload[position + 1];
          position += 2;
          if (!length || position + length > payload.length) throw new Error("malformed H265 aggregation packet");
          nals.push(payload.slice(position, position + length));
          position += length;
        }
        return nals;
      }
      if (type === 49) {
        if (payload.length < 4) throw new Error("malformed H265 fragmentation unit");
        return this.fragment({
          start: !!(payload[2] & 128),
          end: !!(payload[2] & 64),
          discontinuity,
          // Preserve F, layer id and temporal id; restore the original type.
          header: Uint8Array.of((payload[0] & 0x81) | ((payload[2] & 63) << 1), payload[1]),
          body: payload.slice(3),
          name: "H265 fragmentation unit",
        });
      }
      throw new Error("unsupported H265 packetization");
    }

    h264Nals(payload, discontinuity) {
      const type = payload[0] & 31;
      if (type >= 1 && type <= 23) return [payload];
      if (type === 24) {
        const nals = [];
        let position = 1;
        if (position === payload.length) throw new Error("malformed STAP-A: empty aggregate");
        while (position < payload.length) {
          if (position + 2 > payload.length) throw new Error("malformed STAP-A");
          const length = (payload[position] << 8) | payload[position + 1];
          position += 2;
          if (!length || position + length > payload.length) throw new Error("malformed STAP-A");
          nals.push(payload.slice(position, position + length));
          position += length;
        }
        if (position !== payload.length) throw new Error("malformed STAP-A");
        return nals;
      }
      if (type === 28) {
        if (payload.length < 3) throw new Error("malformed FU-A");
        return this.fragment({
          start: !!(payload[1] & 128),
          end: !!(payload[1] & 64),
          discontinuity,
          header: Uint8Array.of((payload[0] & 224) | (payload[1] & 31)),
          body: payload.slice(2),
          name: "FU-A",
        });
      }
      throw new Error("unsupported H264 packetization");
    }

    classifyNal(nal, codec) {
      if (codec === "h265") {
        const type = (nal[0] >> 1) & 63;
        if (type === 32) this.vps = nal;
        if (type === 33) this.sps = nal;
        if (type === 34) this.pps = nal;
        // IRAP pictures: BLA_W_LP through CRA_NUT.
        if (type >= 16 && type <= 21) this.auHasIdr = true;
        return;
      }
      const type = nal[0] & 31;
      if (type === 7) this.sps = nal;
      if (type === 8) this.pps = nal;
      if (type === 5) this.auHasIdr = true;
    }

    parameterPrefix(codec) {
      if (codec === "h265") {
        return this.vps && this.sps && this.pps ? [this.vps, this.sps, this.pps] : null;
      }
      return this.sps && this.pps ? [this.sps, this.pps] : null;
    }
  }

  function monotonicNow() {
    return typeof performance !== "undefined" && typeof performance.now === "function"
      ? performance.now()
      : Date.now();
  }

  function installWorker(scope) {
    const core = new RtpWorkerCore((message) => {
      const transfer = message.buffer ? [message.buffer] : [];
      scope.postMessage(message, transfer);
    });
    let flushTimer = null;
    const clearFlushTimer = () => {
      if (flushTimer !== null) clearTimeout(flushTimer);
      flushTimer = null;
    };
    const reportError = (error) => scope.postMessage({
      type: "error",
      mediaEpoch: core.info?.mediaEpoch,
      message: String(error.message).slice(0, 256),
    });
    const scheduleFlush = () => {
      clearFlushTimer();
      const delay = core.nextFlushDelay();
      if (delay === null) return;
      flushTimer = setTimeout(() => {
        flushTimer = null;
        try {
          core.flush();
          scheduleFlush();
        } catch (error) {
          reportError(error);
          scheduleFlush();
        }
      }, delay);
    };
    scope.onmessage = (event) => {
      try {
        if (["configure", "streamInfo", "reset"].includes(event.data.type)) clearFlushTimer();
        if (event.data.type === "configure") core.configure(event.data.rtpJitterBuffer);
        else if (event.data.type === "streamInfo") core.streamInfo(event.data.streamInfo);
        else if (event.data.type === "packet") core.packet(event.data.buffer);
        else if (event.data.type === "reset") core.reset();
        scheduleFlush();
      } catch (error) {
        reportError(error);
        scheduleFlush();
      }
    };
    return core;
  }

  class ViewerSession {
    constructor({
      socket,
      worker,
      route = "relay",
      rtpJitterBufferMs,
      audioSink,
      onConfigChanged = () => {},
      onVideo = () => {},
      onAudio,
      setStatus = () => {},
      onStop = () => {},
      onClose = () => {},
    }) {
      this.socket = socket;
      this.worker = worker;
      this.route = route;
      this.audioSink = audioSink;
      this.onConfigChanged = onConfigChanged;
      this.onVideo = onVideo;
      this.onAudio = onAudio || ((message) => this.audioSink?.feed(
        message.buffer,
        message.codec,
        undefined,
        undefined,
        message.timestamp,
      ));
      this.setStatus = (message) => setStatus(boundedStatus(message));
      this.onStop = onStop;
      this.onClose = onClose;
      this.closed = false;
      this.stopRequested = false;
      this.videoBlocked = false;
      this.mediaEpoch = null;
      this.mediaVersion = null;
      if (rtpJitterBufferMs !== undefined) {
        worker.postMessage({ type: "configure", rtpJitterBuffer: normalizeRtpJitterBuffer(rtpJitterBufferMs) });
      }
      worker.onmessage = (event) => this.onWorker(event.data);
    }

    send(message) {
      this.socket.send(JSON.stringify(message));
    }

    open() {
      this.send({ type: "subscribe", id: "subscribe-1", protocolVersion: 1, action: "play", route: this.route });
      this.setStatus(`Route: ${this.route} · waiting for metadata and IDR`);
    }

    onText(text) {
      try {
        this.onControl(JSON.parse(text));
      } catch (_error) {
        this.setStatus("Error: invalid relay control message");
      }
    }

    onControl(message) {
      if (!message || typeof message !== "object") {
        this.setStatus("Error: invalid relay control message");
      } else if (message.type === "streamInfo") {
        const tracks = parseStreamSdp(message.sdp);
        if (!tracks.some((track) => track.kind === "video")) throw new Error("missing video track");
        this.mediaEpoch = message.mediaEpoch;
        this.mediaVersion = message.version;
        this.audioSink?.reset?.();
        this.worker.postMessage({ type: "streamInfo", streamInfo: message });
        const video = tracks.find((track) => track.kind === "video");
        this.setStatus(`Route: ${this.route} · epoch ${message.mediaEpoch} · version ${message.version} · ${video?.width ?? "?"}x${video?.height ?? "?"} · waiting for IDR`);
      } else if (message.type === "discontinuity") {
        this.mediaEpoch = message.mediaEpoch;
        this.mediaVersion = null;
        this.audioSink?.reset?.();
        this.worker.postMessage({ type: "reset" });
        this.setStatus(`Route: ${this.route} · epoch ${message.mediaEpoch} · waiting for IDR`);
      } else if (["controlError", "mediaError", "changeStreamError"].includes(message.type)) {
        this.setStatus(`Error: ${message.message || "relay control failed"}`);
      }
    }

    onBinary(buffer) {
      this.worker.postMessage({ type: "packet", buffer }, [buffer]);
    }

    onWorker(message) {
      const isMediaMessage = ["ready", "configChanged", "video", "audio"].includes(message.type);
      if (isMediaMessage && this.mediaEpoch !== null && message.mediaEpoch !== this.mediaEpoch) return;
      if (isMediaMessage && this.mediaVersion !== null && message.version !== this.mediaVersion) return;
      if (message.type === "ready") {
        this.send({ type: "streamReady", id: "ready-1", mediaEpoch: message.mediaEpoch, version: message.version });
      } else if (message.type === "configChanged") {
        this.setStatus(`Route: ${this.route} · epoch ${message.mediaEpoch} · version ${message.version} · ${message.width ?? "?"}x${message.height ?? "?"} · waiting for IDR`);
        // The callback returns true when it cannot play this configuration
        // (e.g. no browser H.265 decoder); a later "video" message must not
        // then overwrite that bounded status with a misleading "playing".
        this.videoBlocked = !!this.onConfigChanged(message);
      } else if (message.type === "video") {
        this.onVideo({ ...message, buffer: toBytes(message.buffer) });
        if (!this.videoBlocked) {
          this.setStatus(`Route: ${this.route} · epoch ${message.mediaEpoch} · version ${message.version} · playing`);
        }
      } else if (message.type === "audio") {
        this.onAudio({ ...message, buffer: toBytes(message.buffer) });
      } else if (message.type === "error") {
        this.setStatus(`Error: ${message.message || "media worker failed"}`);
      }
    }

    changeStream(streamId) {
      this.send({ type: "changeStream", id: "change-1", streamId: validateStreamId(streamId) });
    }

    pause() {
      this.send({ type: "pause", id: "pause-1" });
    }

    resume() {
      this.audioSink?.unlock();
      this.send({ type: "subscribe", id: "resume-1", protocolVersion: 1, action: "play", route: this.route });
    }

    stop() {
      if (this.stopRequested) return;
      this.stopRequested = true;
      this.socket.close();
      this.onStop();
      this.close();
    }

    close() {
      if (this.closed) return;
      this.closed = true;
      this.worker.terminate();
      this.onClose();
      this.audioSink?.close();
    }
  }

  class ViewerCoordinator {
    constructor({ relayProtocol, createSocket, createSession, sendStop, onError = () => {} }) {
      this.relayProtocol = relayProtocol;
      this.createSocket = createSocket;
      this.createSession = createSession;
      this.sendStop = sendStop;
      this.onError = onError;
      this.viewer = null;
      this.sessionId = null;
      this.startGeneration = 0;
    }

    beginStart() {
      this.startGeneration += 1;
      this.stopActive();
      return this.startGeneration;
    }

    stopActive() {
      const active = this.viewer;
      if (!active) return;
      this.viewer = null;
      this.sessionId = null;
      active.stop();
    }

    acceptStart(generation, relay) {
      if (generation !== this.startGeneration) {
        this.sendStop(relay.sessionId);
        return null;
      }
      this.stopActive();
      const sessionId = relay.sessionId;
      const socket = this.createSocket({ url: buildViewerWsUrl(relay, this.relayProtocol), relay });
      const session = this.createSession({
        socket,
        onStop: () => {
          if (this.viewer === session) {
            this.viewer = null;
            this.sessionId = null;
          }
          this.sendStop(sessionId);
        },
      });
      this.viewer = session;
      this.sessionId = sessionId;
      socket.onopen = () => { if (this.viewer === session) session.open(); };
      socket.onmessage = (event) => {
        if (this.viewer !== session) return;
        typeof event.data === "string" ? session.onText(event.data) : session.onBinary(event.data);
      };
      socket.onclose = () => session.close();
      socket.onerror = () => { if (this.viewer === session) this.onError(); };
      return session;
    }
  }

  return {
    alawByteToPcm16,
    boundedStatus,
    buildViewerWsUrl,
    classifyMediaPacket,
    DEFAULT_RTP_JITTER_BUFFER_MS,
    G711AudioSink,
    installWorker,
    MAX_RTP_JITTER_BUFFER_MS,
    mulawByteToPcm16,
    normalizeRtpJitterBuffer,
    normalizeVideoFps,
    parseCompoundRtcp,
    parseRtpPacket,
    parseStreamSdp,
    RtpJitterBuffer,
    RtpWorkerCore,
    validateStreamId,
    ViewerCoordinator,
    ViewerSession,
  };
}));
